import * as React from 'react';
import { makeStyles, createStyles } from '@material-ui/core/styles';
import { ChartSkeleton, ChartSkeletonVariant, CHART_CARD_MIN_HEIGHT } from '../components/loading/ChartSkeleton';
import { useChartOptions } from './chartOptions/ChartOptionsContext';
import { useApplyChartOptions } from './chartOptions/useApplyChartOptions';

// How far outside the visible area a chart starts loading: a little above (scrolling back up) and about half a row below, so a chart is usually
// ready as it scrolls into view but a dashboard only queries what is on screen plus the next few cards. (Cards are about 560px tall: on a tall
// monitor the visible area alone already holds three rows, so the margin has to stay small.)
export const LOAD_AHEAD_MARGIN = '100px 0px 250px 0px';

type VisibleCallback = () => void;

interface SharedObserver {
    observer: IntersectionObserver;
    callbacks: Map<Element, VisibleCallback>;
}

// One IntersectionObserver per scroll container, shared by every chart on the dashboard.
const sharedObservers = new Map<Element | null, SharedObserver>();

// Rendering for print / "save as PDF" must contain every chart, so a print request loads all of them.
const printCallbacks = new Set<VisibleCallback>();
let isPrintListenerAdded = false;

const addPrintListener = () => {
    if (isPrintListenerAdded || typeof window === 'undefined') {
        return;
    }
    isPrintListenerAdded = true;
    window.addEventListener('beforeprint', () => {
        printCallbacks.forEach((callback) => callback());
    });
};

// IntersectionObserver ignores its rootMargin when the page scrolls inside an inner container and no root is given, so the nearest ancestor
// that really scrolls is the root. An ancestor that merely has overflow:auto but is as tall as its content does not scroll and must not be the
// root: every card would then count as visible inside it and the whole dashboard would load at once. With no scrolling ancestor the browser
// window scrolls, and the viewport (no root) is right.
// A dashboard mounts hundreds of cards in the same frame, all inside the same containers: the answer for an ancestor is kept for that frame instead of
// reading the styles of the whole chain for every card.
let scrollParentCache = new WeakMap<HTMLElement, HTMLElement | null>();
let lastLoggedRoot: HTMLElement | null | undefined;

// Switch on with localStorage.setItem('rv-lazy-debug', '1'): says once which element the dashboard's cards are observed against.
const logObserverRoot = (root: HTMLElement | null) => {
    try {
        if (window.localStorage.getItem('rv-lazy-debug') !== '1' || lastLoggedRoot === root) {
            return;
        }
        lastLoggedRoot = root;
        // tslint:disable-next-line: no-console
        console.info(
            '[lazy charts] observing against',
            root ? `${root.tagName.toLowerCase()}#${root.id}.${String(root.className).split(' ').join('.')}` : 'the browser window',
            root ? { clientHeight: root.clientHeight, scrollHeight: root.scrollHeight, windowHeight: window.innerHeight } : { windowHeight: window.innerHeight },
        );
    } catch (error) {
        // storage can be unavailable; the log is only a debugging aid
    }
};
let isScrollParentCacheScheduled = false;

const getScrollParent = (element: HTMLElement): HTMLElement | null => {
    if (!isScrollParentCacheScheduled) {
        isScrollParentCacheScheduled = true;
        window.requestAnimationFrame(() => {
            scrollParentCache = new WeakMap<HTMLElement, HTMLElement | null>();
            isScrollParentCacheScheduled = false;
        });
    }

    const visited: HTMLElement[] = [];
    let result: HTMLElement | null = null;
    let parent = element.parentElement;
    // <body> and <html> are never the root: their overflow belongs to the window (the browser hands it over), so the body's own box is only one screen
    // tall at the top of the document, and cards below it would never count as visible. The window scrolls, and no root means the window.
    while (parent && parent !== document.body && parent !== document.documentElement) {
        if (scrollParentCache.has(parent)) {
            result = scrollParentCache.get(parent) || null;
            break;
        }
        visited.push(parent);
        const overflowY = window.getComputedStyle(parent).overflowY;
        const canScroll = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
        // A real inner scroller is no taller than the window. A taller element that merely overflows by a few pixels (a negative margin, a rounding)
        // is part of the page that the window scrolls: using it as the root would make every card count as visible at once.
        if (canScroll && parent.scrollHeight > parent.clientHeight + 1 && parent.clientHeight <= window.innerHeight + 1) {
            result = parent;
            break;
        }
        parent = parent.parentElement;
    }
    visited.forEach((ancestor) => scrollParentCache.set(ancestor, result));
    logObserverRoot(result);
    return result;
};

// Charts are mounted from a queue, a couple per frame, never inside the observer's own callback. Mounting a chart runs its render and effects at once, and
// a chart that throws while it mounts used to abort the observer callback for every card after it in the same batch: those cards were already counted as
// visible, so they were never notified again and stayed grey forever, with no query ever sent. Now one failing chart cannot stop the others, and a burst of
// cards coming into view does not freeze the page by mounting them all in one frame.
const MOUNTS_PER_FRAME = 2;
const mountQueue: VisibleCallback[] = [];
let isMountScheduled = false;

const runQueuedMounts = () => {
    isMountScheduled = false;
    for (let mounted = 0; mounted < MOUNTS_PER_FRAME && mountQueue.length > 0; mounted++) {
        const mount = mountQueue.shift();
        try {
            if (mount) {
                mount();
            }
        } catch (error) {
            // the failing chart reports its own error; the next card still gets its turn
            // tslint:disable-next-line: no-console
            console.error(error);
        }
    }
    if (mountQueue.length > 0) {
        scheduleQueuedMounts();
    }
};

const scheduleQueuedMounts = () => {
    if (!isMountScheduled) {
        isMountScheduled = true;
        window.requestAnimationFrame(runQueuedMounts);
    }
};

const observeElement = (element: HTMLElement, onVisible: VisibleCallback): (() => void) => {
    const root = getScrollParent(element);
    let shared = sharedObservers.get(root);

    if (!shared) {
        const callbacks = new Map<Element, VisibleCallback>();
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const callback = callbacks.get(entry.target);
                    if (entry.isIntersecting && callback) {
                        callbacks.delete(entry.target);
                        observer.unobserve(entry.target);
                        mountQueue.push(callback);
                    }
                });
                scheduleQueuedMounts();
            },
            { root, rootMargin: LOAD_AHEAD_MARGIN },
        );
        shared = { observer, callbacks };
        sharedObservers.set(root, shared);
    }

    const { observer, callbacks } = shared;
    callbacks.set(element, onVisible);
    observer.observe(element);

    return () => {
        callbacks.delete(element);
        observer.unobserve(element);
    };
};

// For tests: forget the shared observers and anything still queued, so each test starts from a clean state.
export const resetLazyChartItemState = () => {
    sharedObservers.clear();
    mountQueue.length = 0;
    isMountScheduled = false;
};

const useStyles = makeStyles(() =>
    createStyles({
        // A card keeps its height while its chart loads (a chart's own loading state is often much shorter): when cards collapsed, the rows below
        // moved up into the look-ahead area and started loading too, one row after another, until everything was loading.
        item: {
            minHeight: CHART_CARD_MIN_HEIGHT,
        },
    }),
);

interface LazyChartItemProps {
    // the same skeleton the chart shows itself while it loads, so the card keeps one shape from first paint to data
    skeleton?: ChartSkeletonVariant;
    // the chart's feature name: what lets the dashboard's "Chart options" change this chart's own period switch
    featureName?: string;
    children: React.ReactNode;
}

// The `.item` wrapper every dashboard chart sits in. The chart (and so its query) is only mounted once the card is near the visible area;
// once loaded it stays mounted, so scrolling back never reloads it.
export const LazyChartItem: React.FC<LazyChartItemProps> = ({ skeleton = 'card', featureName, children }) => {
    const classes = useStyles();
    const itemRef = React.useRef<HTMLDivElement>(null);
    const { resetVersion } = useChartOptions();
    const isChangedByDashboardRef = useApplyChartOptions(itemRef, featureName);
    // A reset (or a choice taken away) mounts again only the charts the dashboard changed, so the others do not query again for nothing.
    const remountKeyRef = React.useRef(0);
    if (isChangedByDashboardRef.current && remountKeyRef.current !== resetVersion) {
        remountKeyRef.current = resetVersion;
        isChangedByDashboardRef.current = false;
    }
    const canObserve = typeof IntersectionObserver !== 'undefined';
    const [isLoaded, setIsLoaded] = React.useState<boolean>(!canObserve);

    React.useEffect(() => {
        if (isLoaded || !itemRef.current) {
            return undefined;
        }

        // the card can be gone (panel switched, search typed) by the time its turn in the queue comes
        let isCancelled = false;
        const markLoaded = () => {
            if (!isCancelled) {
                setIsLoaded(true);
            }
        };
        addPrintListener();
        printCallbacks.add(markLoaded);
        const stopObserving = observeElement(itemRef.current, markLoaded);

        return () => {
            isCancelled = true;
            printCallbacks.delete(markLoaded);
            stopObserving();
        };
    }, [isLoaded]);

    return (
        <div className={`item ${classes.item}`} ref={itemRef}>
            {isLoaded ? <React.Fragment key={remountKeyRef.current}>{children}</React.Fragment> : <ChartSkeleton variant={skeleton} animated={false} />}
        </div>
    );
};
